import Cookies from 'universal-cookie';

import { nullify } from '@/utils/nullify';

import getHost from './utils/host/get-host';

import type { NextIncomingMessage } from './types/request';
import type {
  Cookie,
  CookieGetOptions,
  CookieSetOptions,
} from 'universal-cookie';

/**
 * Helper function to build standardized cookie options.
 * Filters out undefined values to prevent TypeError in cookie library.
 * @private
 */
const _buildCookieOptions = (
  req?: NextIncomingMessage,
  options?: CookieSetOptions
): CookieSetOptions => {
  const baseOptions: CookieSetOptions = { path: '/' };
  const domain = getHost(req);

  if (domain) {
    baseOptions.domain = domain;
  }

  // Clean options and merge with base options
  const cleanedOptions = options
    ? nullify<CookieSetOptions>(options as Record<string, unknown>)
    : {};

  const mergedOptions = {
    ...baseOptions,
    ...cleanedOptions,
  };

  // Filter out any undefined values to prevent toLowerCase errors
  // in the underlying cookie library (DEV-4720)
  return Object.fromEntries(
    Object.entries(mergedOptions).filter(([_, value]) => value !== undefined)
  ) as CookieSetOptions;
};

/**
 * Get a cookie value.
 *
 * On the client this takes the cookie from `document.cookie`, on the server it
 * takes it from the Next request object.
 */
export const getUniversalCookie = <
  T extends string | number | boolean | object,
>(
  cookieName: string,
  {
    req,
    options,
  }: { req?: NextIncomingMessage; options?: CookieGetOptions } = {}
): T | undefined => {
  if (typeof window !== 'undefined') {
    return new Cookies(document.cookie).get<T>(cookieName, options);
  }

  if (!req) {
    return;
  }

  return new Cookies(req.headers?.cookie).get<T>(cookieName, options);
};

/**
 * Set a cookie value.
 */
export const setUniversalCookie = (
  cookieName: string,
  value: Cookie,
  {
    options,
    req,
  }: { options?: CookieSetOptions; req?: NextIncomingMessage } = {}
) => {
  if (typeof window !== 'undefined') {
    const clientOptions = _buildCookieOptions(req, options);

    new Cookies(document.cookie).set(cookieName, value, clientOptions);

    return;
  }

  // Server-side: Check for req *before* building options
  if (!req) {
    throw new Error(
      'A Next.js request object is required to set a cookie from the server.'
    );
  }

  const finalOptions = _buildCookieOptions(req, options);

  new Cookies(req.headers?.cookie).set(cookieName, value, finalOptions);
};

/**
 * Remove a cookie value.
 */
export const removeUniversalCookie = (
  cookieName: string,
  {
    req,
    options,
  }: { req?: NextIncomingMessage; options?: CookieSetOptions } = {}
) => {
  if (typeof window !== 'undefined') {
    const clientOptions = _buildCookieOptions(req, options);
    // Construct options for the specific API domain case separately for client
    const clientOptionsApi: CookieSetOptions = {
      ..._buildCookieOptions(req, options),
      domain: 'api.uppbeat.io',
    };
    // Cookies written via SSR `Set-Cookie` headers have no Domain attribute
    // (host-only); browsers store those separately from domain-scoped cookies,
    // so a removal with an explicit domain leaves them behind (DEV-6240).
    const { domain: _domain, ...clientOptionsHostOnly } = clientOptions;

    new Cookies(document.cookie).remove(cookieName, clientOptions);
    new Cookies(document.cookie).remove(cookieName, clientOptionsApi);
    new Cookies(document.cookie).remove(cookieName, clientOptionsHostOnly);

    return;
  }

  // Server-side: Check for req *before* building options
  if (!req) {
    throw new Error(
      'A Next.js request object is required to remove a cookie from the server.'
    );
  }

  const finalOptions = _buildCookieOptions(req, options);
  // Construct options for the specific API domain case separately for server
  const finalOptionsApi: CookieSetOptions = {
    ..._buildCookieOptions(req, options),
    domain: 'api.uppbeat.io',
  };

  new Cookies(req.headers?.cookie).remove(cookieName, finalOptions);
  // Also attempt removal for the specific API domain on the server if needed
  // Depending on how cookies are handled server-side, this might be redundant
  // but mirroring client logic for robustness.
  new Cookies(req.headers?.cookie).remove(cookieName, finalOptionsApi);
};
